import { getLocalTimeZone, type DateValue } from '@internationalized/date';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import { getContext } from 'svelte';
import { get } from 'svelte/store';

import type { FilteredValues, OptionList } from '$saga/types/search.types';

import { defaultFormLabels, defaultFormValues } from './constants';
import { filteredToursCount, filtersContext } from './store';
import {
    Sorting,
    type FilterFormLabels,
    type FilterFormValues,
    type LandingPageContext,
} from './types';

const searchQueryKeyByFormValueKey: Record<keyof FilterFormValues, string> = {
    destination: 'destination',
    theme: 'theme',
    startDate: 'startDate',
    endDate: 'endDate',
    months: 'month',
    flexibility: 'flexibility',
    nbPax: 'nbPax',
    levels: 'level',
    types: 'type',
    tags: 'tag',
    resorts: 'resort',
    budgetMin: 'budgetMin',
    budgetMax: 'budgetMax',
    durationMin: 'durationMin',
    durationMax: 'durationMax',
    bestReviewed: 'bestReviewed',
    exclusiveSport: 'exclusiveSport',
    keywords: 'keywords',
};

const isDateValue = (value: unknown): value is DateValue =>
    typeof value === 'object' &&
    value !== null &&
    'year' in value &&
    'month' in value &&
    'day' in value;

const formatSearchQueryValue = (value: unknown): string => {
    if (isDateValue(value)) {
        return `${String(value.day).padStart(2, '0')}/${String(
            value.month,
        ).padStart(2, '0')}/${value.year}`;
    }

    return String(value);
};

export const isAlpineSkiingSelected = (
    selectedSports: string[],
    alpineSkiingSportId: string | null,
): boolean =>
    null !== alpineSkiingSportId &&
    selectedSports.includes(alpineSkiingSportId);

/**
 * Format selected value ids to their corresponding label
 * @param currentValue
 * @param options
 */
export const findCurrentSelectedFilterLabel = (
    currentValue: string,
    options: FilteredValues | OptionList,
) => {
    if (currentValue === '') return '';

    return (
        options.find((currentOption) => currentOption.value === currentValue)
            ?.label ?? ''
    );
};

/**
 * Initialize form with selected values
 * @param values
 * @param defaultDestinationOptions
 * @param defaultSportOptions
 *
 * @param defaultLevels
 * @param defaultTypes
 * @param defaultTags
 * @param defaultDurationMin
 * @param defaultDurationMax
 * @param defaultBudgetMin
 * @param defaultBudgetMax
 */
export const initializeLabelValues = (
    values: FilterFormValues,
    defaultDestinationOptions: OptionList,
    defaultSportOptions: OptionList,
    defaultLevels: OptionList,
    defaultTypes: OptionList,
    defaultTags: OptionList,
    defaultDurationMin: number,
    defaultDurationMax: number,
    defaultBudgetMin: number,
    defaultBudgetMax: number,
): FilterFormLabels => {
    const destinationLabels = values.destination.map((dest) =>
        dest === 'all'
            ? 'Toutes les destinations'
            : findCurrentSelectedFilterLabel(dest, defaultDestinationOptions),
    );
    const sportLabels = values.theme.map((theme) =>
        theme === 'all'
            ? 'Tous les sports'
            : findCurrentSelectedFilterLabel(theme, defaultSportOptions),
    );

    const monthLabels = values.months.map((month) => {
        const [year, monthNum] = month.split('-');
        const date = dayjs()
            .set('year', parseInt(year))
            .set('month', parseInt(monthNum) - 1);
        return date.format('MMM YYYY');
    });

    // Secondary filters
    const levelsLabel = values.levels
        .map((val) => findCurrentSelectedFilterLabel(val, defaultLevels))
        .join(', ');

    const tagsLabel = values.tags
        .map((val) => findCurrentSelectedFilterLabel(val, defaultTags))
        .join(', ');

    const typesLabel = values.types
        .map((val) => findCurrentSelectedFilterLabel(val, defaultTypes))
        .join(', ');

    return {
        destination: destinationLabels,
        theme: sportLabels,
        startDate: values.startDate,
        endDate: values.endDate,
        months: monthLabels,
        nbPax: values.nbPax,
        // secondary
        levels: levelsLabel,
        types: typesLabel,
        duration: `${String(values.durationMin ?? defaultDurationMin)}ms -
            ${String(values.durationMax ?? defaultDurationMax)}ms`,
        budget: `${String(
            values.budgetMin ?? defaultBudgetMin,
        )} - ${String(values.budgetMax ?? defaultBudgetMax)}`,
        tags: tagsLabel,
    };
};

/**
 * Reset form with initial default values
 */
export const resetLabels = () => defaultFormLabels;

export const resetSecondaryLabels = () => ({
    levels: defaultFormLabels.levels,
    types: defaultFormLabels.types,
    duration: defaultFormLabels.duration,
    budget: defaultFormLabels.budget,
    tags: defaultFormLabels.tags,
});
/**
 * Reset form with initial default labels
 */
export const resetValues = () => defaultFormValues;

export const resetSecondaryValues = () => ({
    levels: defaultFormValues.levels,
    types: defaultFormValues.types,
    durationMin: defaultFormValues.durationMin,
    durationMax: defaultFormValues.durationMax,
    budgetMin: defaultFormValues.budgetMin,
    budgetMax: defaultFormValues.budgetMax,
    tags: defaultFormValues.tags,
    resorts: defaultFormValues.resorts,
});

/**
 * Search pages: a filter change auto submits the form (Turbo stream) and resets to page 1.
 */
const handleFilterChange = () => {
    filtersContext.set({
        ...get(filtersContext),
        isSubmitting: true,
    });
    const form = document.getElementById(
        SEARCH_PAGE_FORM_ID,
    ) as HTMLFormElement;
    if (form) {
        form.requestSubmit();
    }
};

export const debouncedHandleFilterChange = debounce(handleFilterChange, 500);

/**
 * Homepage: filters only update the tour count, the submit is driven by the "Rechercher" button.
 */
export const homeFilterChangeHandler = debounce(
    () => updateFilteredToursCount(get(filtersContext).formValues),
    500,
);

export const FILTER_CHANGE_CONTEXT = Symbol('filterChange');

// Roots that don't provide a handler (e.g. the sidebar badges) fall back to the search submit.
export const getFilterChangeHandler = (): (() => void) =>
    getContext<(() => void) | undefined>(FILTER_CHANGE_CONTEXT) ??
    debouncedHandleFilterChange;

export const handleFormSubmissionAnalytics = () => {
    if (!('dataLayer' in window)) {
        return;
    }
    // TODO send this "filter" analytic as multiple events ?
    // let levelLabel =
    //     filtersContext.formValues.levels.map((level) =>
    //         findCurrentSelectedFilterLabel(level, defaultOptions.levels),
    //     ) ?? 'without_search';
    // let monthLabel = 'without_search';
    // let tagLabel =
    //     filtersContext.formValues.tags.map((tag) =>
    //         findCurrentSelectedFilterLabel(tag, defaultOptions.tags),
    //     ) ?? 'without_search';
    // let typeLabel =
    //     filtersContext.formValues.types.map((type) =>
    //         findCurrentSelectedFilterLabel(type, defaultOptions.types),
    //     ) ?? 'without_search';
    // const secondaryFilters = [
    //     { name: 'levels', value: levelLabel },
    //     { name: 'month', value: monthLabel },
    //     { name: 'themes', value: tagLabel },
    //     { name: 'types', value: typeLabel },
    //     {
    //         name: 'duration',
    //         value: `${filtersContext.formValues.durationMin} - ${filtersContext.formValues.durationMax} jours`,
    //     },
    //     {
    //         name: 'budget',
    //         value: `${filtersContext.formValues.budgetMin} - ${filtersContext.formValues.budgetMax} €`,
    //     },
    // ];
    // for (const filter of secondaryFilters) {
    //     window.dataLayer.push({
    //         event: 'santianoEcommerce',
    //         eventCommand: 'filter',
    //         filterName: filter.name,
    //         filterValue: filter.value,
    //     });
    // }

    const filtersContextStore = get(filtersContext);
    const departureDate =
        filtersContextStore.formValues.startDate?.toString() ??
        'without_search';
    let travelers =
        filtersContextStore.formValues.nbPax?.toString() ?? 'without_search';
    const destinationLabels =
        filtersContextStore.formValues.destination.map((destination) =>
            findCurrentSelectedFilterLabel(
                destination,
                filtersContextStore.defaultDestinationOptions ?? [],
            ),
        ) ?? 'without_search';
    const sportLabels =
        filtersContextStore.formValues.theme.map((theme) =>
            findCurrentSelectedFilterLabel(
                theme,
                filtersContextStore?.defaultSportOptions ?? [],
            ),
        ) ?? 'without_search';

    if (travelers === '0') {
        travelers = 'without_search';
    }
    // eslint-disable-next-line
    // @ts-ignore
    window.dataLayer.push({
        event: 'santianoEcommerce',
        eventCommand: 'search',
        destinations: destinationLabels,
        sports: sportLabels,
        departureDate: departureDate,
        travelers: travelers,
    });
};

export const SEARCH_PAGE_FORM_ID = 'travel-search-engine-form';

const getSearchForm = () =>
    document.getElementById(SEARCH_PAGE_FORM_ID) as HTMLFormElement;

const getGenericSearchUrl = () => getSearchForm().action;

let latestCountRequest = 0;

export async function updateFilteredToursCount(
    formValues: FilterFormValues,
): Promise<void> {
    const requestId = ++latestCountRequest;

    try {
        const queryParams = new URLSearchParams();
        Object.entries(formValues).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                const queryKey =
                    searchQueryKeyByFormValueKey[key as keyof FilterFormValues];
                if (Array.isArray(value)) {
                    value.forEach((item) =>
                        queryParams.append(`${queryKey}[]`, String(item)),
                    );
                } else {
                    queryParams.append(queryKey, formatSearchQueryValue(value));
                }
            }
        });
        const keywords = document.getElementById(
            'keywords',
        ) as HTMLInputElement | null;
        if (keywords?.value) {
            queryParams.set('keywords', keywords.value);
        }
        const url = `${getSearchForm().dataset.countUrl}?${queryParams.toString()}`;
        const response = await fetch(url, {
            headers: {
                Accept: 'text/vnd.turbo-stream.html',
            },
            method: 'GET',
        });

        if (response.ok) {
            const data = await response.json();
            if (requestId === latestCountRequest) {
                filteredToursCount.set(data.count);
            }
        } else if (requestId === latestCountRequest) {
            filteredToursCount.set(null);
        }
    } catch {
        if (requestId === latestCountRequest) {
            filteredToursCount.set(null);
        }
    }
}

/**
 * Check if removing a filter should trigger a redirect to generic search
 * Returns true if the filter being removed is a pillar filter
 */
export const shouldRedirectOnRemoval = (
    filterType: 'sport' | 'destination' | 'tag' | 'date',
    filterId?: string,
): boolean => {
    if (!get(filtersContext).isLandingPage) {
        return false;
    }

    const pillarFilters = get(filtersContext).pillarFilters;
    switch (filterType) {
        case 'sport':
            return pillarFilters.sports?.includes(filterId ?? '') ?? false;
        case 'destination':
            return (
                pillarFilters.destinations?.includes(filterId ?? '') ?? false
            );
        case 'tag':
            return pillarFilters.tags?.includes(filterId ?? '') ?? false;
        case 'date':
            return pillarFilters.hasDateRange ?? false;
        default:
            return false;
    }
};

/**
 * Redirects to the generic search page with current filters preserved
 * This is used when a pillar filter is removed from a landing page
 * @param currentFilters Current filter values from the store
 */
export const redirectToGenericSearch = (
    currentFilters: FilterFormValues,
): void => {
    // Build URL query parameters from current filters
    const params = new URLSearchParams();

    // Add destinations
    if (currentFilters.destination && currentFilters.destination.length > 0) {
        currentFilters.destination.forEach((dest) => {
            if (dest && dest !== '') {
                params.append('destination[]', dest);
            }
        });
    }

    // Add sports (mapped to 'theme' in form)
    if (currentFilters.theme && currentFilters.theme.length > 0) {
        currentFilters.theme.forEach((sport) => {
            if (sport && sport !== '') {
                params.append('theme[]', sport);
            }
        });
    }

    // Add dates
    if (currentFilters.startDate && currentFilters.endDate) {
        const startDateFormatted =
            currentFilters.startDate.toDate(getLocalTimeZone());
        const endDateFormatted =
            currentFilters.endDate.toDate(getLocalTimeZone());
        params.append(
            'startDate',
            `${String(startDateFormatted.getDate()).padStart(2, '0')}/${String(startDateFormatted.getMonth() + 1).padStart(2, '0')}/${startDateFormatted.getFullYear()}`,
        );
        params.append(
            'endDate',
            `${String(endDateFormatted.getDate()).padStart(2, '0')}/${String(endDateFormatted.getMonth() + 1).padStart(2, '0')}/${endDateFormatted.getFullYear()}`,
        );
    }

    // Add flexibility
    if (currentFilters.flexibility && currentFilters.flexibility !== 0) {
        params.append('flexibility', String(currentFilters.flexibility));
    }

    // Add number of travelers
    if (
        currentFilters.nbPax &&
        currentFilters.nbPax !== '0' &&
        currentFilters.nbPax !== '1'
    ) {
        params.append('nbPax', currentFilters.nbPax);
    }

    // Add levels
    if (currentFilters.levels && currentFilters.levels.length > 0) {
        currentFilters.levels.forEach((level) => {
            if (level) {
                params.append('level[]', String(level));
            }
        });
    }

    // Add types
    if (currentFilters.types && currentFilters.types.length > 0) {
        currentFilters.types.forEach((type) => {
            if (type) {
                params.append('type[]', String(type));
            }
        });
    }

    // Add tags
    if (currentFilters.tags && currentFilters.tags.length > 0) {
        currentFilters.tags.forEach((tag) => {
            if (tag) {
                params.append('tag[]', String(tag));
            }
        });
    }

    if (
        isAlpineSkiingSelected(
            currentFilters.theme,
            get(filtersContext).alpineSkiingSportId,
        )
    ) {
        currentFilters.resorts.forEach((resort) => {
            params.append('resort[]', resort);
        });
    }

    // Add budget (only if different from default)
    if (
        currentFilters.budgetMin &&
        currentFilters.budgetMax &&
        (currentFilters.budgetMin !== defaultFormValues.budgetMin ||
            currentFilters.budgetMax !== defaultFormValues.budgetMax)
    ) {
        params.append('budgetMin', String(currentFilters.budgetMin));
        params.append('budgetMax', String(currentFilters.budgetMax));
    }

    // Add duration (only if different from default)
    if (
        currentFilters.durationMin &&
        currentFilters.durationMax &&
        (currentFilters.durationMin !== defaultFormValues.durationMin ||
            currentFilters.durationMax !== defaultFormValues.durationMax)
    ) {
        params.append('durationMin', String(currentFilters.durationMin));
        params.append('durationMax', String(currentFilters.durationMax));
    }

    // Build final URL
    const queryString = params.toString();
    const targetUrl = queryString
        ? `${getGenericSearchUrl()}?${queryString}`
        : getGenericSearchUrl();

    // Perform full page redirect
    window.location.href = targetUrl;
};

export const getSortValue = (landingPageContext: LandingPageContext) => {
    let sort = Sorting.DEFAULT;
    if (landingPageContext.isLandingPage) {
        switch (landingPageContext.type) {
            case 'sport':
                sort = Sorting.SORT_SPORT_RANKING;
                break;
            case 'destination':
                sort = Sorting.SORT_LANDING_DESTINATION_PERSONALIZED;
                break;
            case 'theme':
                sort = Sorting.SORT_LANDING_TAG_PERSONALIZED;
                break;
            default:
                sort = Sorting.SORT_LANDING_PERSONALIZED;
                break;
        }
    }

    return sort;
};

export const handleResetValues = () => {
    // Check if we're on a landing page - if yes, redirect to generic search
    if (get(filtersContext).isLandingPage) {
        // Redirect to generic search without any filters
        window.location.href = getGenericSearchUrl();
    } else {
        // Normal reset behavior
        filtersContext.set({
            ...get(filtersContext),
            formLabels: resetLabels(),
            formValues: resetValues(),
        });
    }
};

export const handleResetSecondaryValues = () => {
    // Check if we're on a landing page - if yes, redirect to generic search
    if (get(filtersContext).isLandingPage) {
        // Redirect to generic search without any filters
        window.location.href = getGenericSearchUrl();
    } else {
        filtersContext.set({
            ...get(filtersContext),
            formLabels: {
                ...get(filtersContext).formLabels,
                ...resetSecondaryLabels(),
            },
            formValues: {
                ...get(filtersContext).formValues,
                ...resetSecondaryValues(),
            },
        });
        debouncedHandleFilterChange();
    }
};
